import React, { useEffect, useState } from 'react';
import { SectionCard } from '../../design-system/components/section/SectionCard';
import { TrendIcon } from '../../design-system/components/section/SectionIcons';
import { getDealComparison, type DealComparison, type DealSource } from '../../services/dealComparison';

const control = 'min-h-[44px] rounded-ca-md px-3 py-2 text-[13px] font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent';
const SOURCES: { value: DealSource; label: string }[] = [
  { value: 'apt_trade', label: '아파트 매매' },
  { value: 'silv_trade', label: '분양권 거래' },
];

export function dealAmount(value: number): string {
  const eok = Math.floor(value / 10000);
  const rest = value % 10000;
  return `${eok ? `${eok}억` : ''}${eok && rest ? ' ' : ''}${rest ? `${rest.toLocaleString('ko-KR')}만원` : '원'}`;
}

function statusText(data: DealComparison): string {
  if (data.status === 'partial') return '일부 거래 자료를 가져오지 못했어요. 전체 자료가 확인될 때까지 요약 가격을 표시하지 않습니다.';
  if (data.status === 'no_data' && data.excluded_rights_count) return '입주권 또는 권리 종류가 확인되지 않은 자료를 제외하면 표시할 분양권 거래가 없어요.';
  if (data.status === 'no_data') return '선택한 기간과 면적에 맞는 거래가 없어요. 아직 신고되지 않은 거래가 있을 수 있습니다.';
  if (data.reason === 'unsupported_region') return '이 지역의 실거래 연결을 준비하고 있어요.';
  if (data.status === 'unavailable') return '이 단지의 실거래 자료를 확인할 수 없어요. 단지와 주소가 확인된 자료만 제공합니다.';
  return '';
}

/** Public transaction evidence is independent of benefit consent and never submits an inquiry. */
export function RealDealComparison({ propertyId, contactable }: { propertyId: number; contactable: boolean }) {
  const [source, setSource] = useState<DealSource>('apt_trade');
  const [typeName, setTypeName] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [result, setResult] = useState<{ key: string; data: DealComparison } | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const queryKey = `${propertyId}:${source}:${typeName}:${attempt}`;
  const data = result?.key === queryKey ? result.data : null;
  // Keep controls mounted while a different area/source loads, including on error.
  const typeData = result?.data.property_id === propertyId ? result.data : null;
  const failed = failedKey === queryKey;
  const loading = !data && !failed;

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    getDealComparison(propertyId, source, typeName || undefined, controller.signal)
      .then((response) => { if (active) setResult({ key: queryKey, data: response }); })
      .catch(() => { if (active) setFailedKey(queryKey); });
    return () => { active = false; controller.abort(); };
  }, [propertyId, source, typeName, queryKey]);

  const summary = data?.status === 'ok' ? data.summary : null;
  const median = summary && summary.count >= 3 ? summary.median_manwon : null;
  return (
    <div id="real-deals" className="scroll-mt-28">
      <SectionCard title="최근 6개월 실거래" icon={<TrendIcon />}>
        <p className="mb-3 text-[13px] leading-5 text-muted">실제로 거래된 가격을 확인하고 분양 조건과 함께 살펴보세요.</p>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2" role="group" aria-label="거래 종류">
              {SOURCES.map((option) => (
                <button key={option.value} type="button" aria-pressed={source === option.value}
                  className={`${control} ${source === option.value ? 'border border-accent-dark bg-accent-soft text-ink' : 'border border-ca-line bg-frame text-muted'}`}
                  onClick={() => setSource(option.value)}>{option.label}</button>
              ))}
            </div>
            {typeData && typeData.available_types.length > 0 && (
              <label className="flex flex-wrap items-center gap-2 text-[13px] font-bold text-body">
                전용면적
                <select className={`${control} max-w-full flex-1 border border-ca-line bg-frame text-ink`}
                  value={typeName || typeData.selected_type?.type_name || ''} onChange={(event) => setTypeName(event.target.value)}>
                  {!typeData.selected_type && <option value="">면적 선택</option>}
                  {typeData.available_types.map((item) => <option key={item.type_name} value={item.type_name}>{item.type_name} · {item.exclusive_area}㎡</option>)}
                </select>
              </label>
            )}
            <div aria-live="polite" aria-busy={loading}>
              {loading && <p className="rounded-ca-lg bg-ca-surface p-4 text-[13px] text-muted" role="status">공식 거래 자료를 확인하고 있어요…</p>}
              {failed && <p className="text-[13px] text-body" role="alert">자료를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.</p>}
              {data && statusText(data) && <p className="rounded-ca-lg bg-ca-surface p-3 text-[13px] leading-5 text-body">{statusText(data)}</p>}
              {data && (
                <>
                  <p className="my-2 text-[12px] leading-5 text-muted">계약일 {data.period.start} ~ {data.period.end} · {data.source_label}</p>
                  {!!data.excluded_rights_count && <p className="mb-2 text-[12px] text-muted">입주권·권리 구분 미확인 {data.excluded_rights_count}건 제외</p>}
                  {summary && (
                    <div className="rounded-ca-lg bg-ca-surface p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[13px] font-bold text-body">{data.selected_type ? '선택 면적 거래' : '확인된 거래'} {summary.count}건</span>
                        {median != null && <span className="text-[12px] text-muted">취소 거래 제외</span>}
                      </div>
                      {median != null ? <p className="mt-2 text-[24px] font-extrabold text-ink"><span className="mr-2 text-[13px] font-medium text-muted">중앙값</span><span className="num">{dealAmount(median)}</span></p>
                        : <p className="mt-2 text-[13px] text-muted">거래가 3건 미만이거나 비교 면적이 확인되지 않아 요약 가격을 표시하지 않습니다.</p>}
                    </div>
                  )}
                  {data.records.length > 0 && (
                    <details className="mt-3 rounded-ca-lg border border-ca-line" open>
                      <summary className="min-h-[44px] cursor-pointer px-3 py-3 text-[13px] font-bold text-body">거래 내역 {data.records.length}건</summary>
                      <div className="max-h-[420px] overflow-auto px-3 pb-2" tabIndex={0} aria-label="실거래 내역">
                        <table className="w-full table-fixed text-left text-[13px]">
                          <caption className="sr-only">{data.source_label} 계약일·전용면적·층·거래 금액. 취소 거래는 집계에서 제외됩니다.</caption>
                          <thead className="text-muted"><tr><th className="w-[44%] py-2 font-medium">계약일 / 면적·층</th><th className="py-2 text-right font-medium">거래 금액</th></tr></thead>
                          <tbody className="divide-y divide-ca-line">
                            {data.records.map((record, index) => (
                              <tr key={`${record.contract_date}-${record.exclusive_area}-${record.floor}-${index}`} className="text-body">
                                <td className="py-4 pr-2 align-top"><span className="num font-medium text-ink">{record.contract_date}</span><br /><span className="text-muted">{record.exclusive_area}㎡ · {record.floor == null ? '층 미제공' : `${record.floor}층`}{record.rights_type ? ` · ${record.rights_type}` : ''}</span></td>
                                <td className="py-4 text-right align-top"><span className={`num text-[17px] font-bold leading-6 tracking-tight sm:text-[19px] ${record.cancelled ? 'text-muted line-through' : 'text-ink'}`}>{dealAmount(record.amount_manwon)}</span><br />
                                  <span className={record.cancelled ? 'text-red-700' : 'text-muted'}>{record.cancelled ? `취소${record.cancel_date ? ` ${record.cancel_date}` : ''}` : record.transaction_method || '거래방식 미제공'}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  )}
                  <p className="mt-3 text-[12px] leading-5 text-muted">출처: 국토교통부 실거래가 공개자료{data.fetched_at ? ` · 조회 ${data.fetched_at.slice(0, 10)}` : ''}<br />동일 단지명·주소 기준. 면적 허용차 ±{data.area_tolerance_sqm}㎡이며, 같은 면적의 다른 타입이 포함될 수 있습니다. 신고 지연·해제로 달라질 수 있고 층·향·옵션에 따라 가격 차이가 있습니다. 실거래는 현재 시세나 수익을 보장하지 않습니다.</p>
                </>
              )}
            </div>
            {(failed || data?.status === 'partial' || data?.status === 'unavailable') && <button type="button" className={`${control} border border-ca-line text-body`} onClick={() => setAttempt((n) => n + 1)}>다시 확인</button>}
          </div>
        <div className="mt-4 border-t border-ca-line pt-3">
          <p className="text-[13px] leading-5 text-body">현재 분양 조건은 별도로 확인해 주세요. 할인 적용 대상과 옵션·추가 비용에 따라 실제 계약 금액이 달라집니다.</p>
          {contactable && <a href="#inquiry" className={`${control} mt-2 flex items-center justify-center bg-accent-soft text-accent-dark`}>내 조건으로 분양가 상담</a>}
        </div>
      </SectionCard>
    </div>
  );
}
